import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { 
  PhoneForwarded, 
  Plus, 
  RefreshCw, 
  Loader2, 
  AlertCircle, 
  RotateCcw,
  ChevronDown,
  XCircle,
  ChevronUp,
  Phone,
  Clock,
  Users,
  Target,
  Info,
  Search,
  X
} from 'lucide-react';
import NotesModal from '../NotesModal/NotesModal';
import './BatchCallPanel.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Normalize phone numbers: remove all non-digits, keep last 10 digits
const normalizePhone = (phone) => {
  if (!phone) return '';
  const digitsOnly = phone.replace(/\D/g, '');
  return digitsOnly.slice(-10);
};

const BatchCallPanel = forwardRef(({ onShowToast, onStatusUpdate }, ref) => {
  const [batchCalls, setBatchCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [batchDetails, setBatchDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});
  const [creating, setCreating] = useState(false);
  // Polled per-recipient statuses (from conversation status endpoint, same as single calls)
  const [recipientPolledStatuses, setRecipientPolledStatuses] = useState({});
  // Polled per-recipient call_successful (from conversation status endpoint)
  const [recipientCallSuccessful, setRecipientCallSuccessful] = useState({});
  // Track which conversation_ids are already being polled
  const [pollingSet, setPollingSet] = useState(new Set());
  // Recipient lead statuses and notes (conversationId -> { leadStatus, notes })
  const [recipientLeadStatuses, setRecipientLeadStatuses] = useState({});
  // Modal for viewing notes
  const [notesModalData, setNotesModalData] = useState(null); // null or { phoneNumber, leadStatus, notes }

  // Create form state
  const [callName, setCallName] = useState('');
  const [recipientInput, setRecipientInput] = useState('');

  // Fetch lead statuses by conversation IDs
  const fetchRecipientStatuses = useCallback(async (conversationIds) => {
    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No auth token found. Cannot fetch recipient statuses.');
        return;
      }
      
      // Build query string with conversation IDs
      const convParams = conversationIds.map(id => `conversationIds=${encodeURIComponent(id)}`).join('&');
      const response = await fetch(`${API_BASE}/agent-statuses?${convParams}`, {
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success && data.data) {
        // Build a map of conversationId -> { leadStatus, notes }
        const statusMap = {};
        const dataArray = Array.isArray(data.data) ? data.data : Object.values(data.data);
        dataArray.forEach(item => {
          if (item.conversationId) {
            statusMap[item.conversationId] = {
              leadStatus: item.leadStatus,
              notes: item.notes
            };
          }
        });
        
        // Merge with existing statuses to keep things cumulative
        setRecipientLeadStatuses(prev => ({ ...prev, ...statusMap }));
      }
    } catch (err) {
      console.error('Error fetching recipient statuses:', err);
    }
  }, []);

  const fetchBatchCalls = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/elevenlabs/batch-calls?limit=20`, {
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        setBatchCalls(data.data?.batch_calls || []);
      }
    } catch (err) {
      console.error('Error fetching batch calls:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync recipient statuses back to Dashboard when batch details load
  // For WhatsApp batch calls the API returns whatsapp_user_id instead of phone_number
  const syncStatusesToDashboard = useCallback((details, polledStatuses = {}) => {
    if (!onStatusUpdate || !details?.recipients) return;
    const statuses = {};
    details.recipients.forEach(r => {
      const phoneKey = r.phone_number || r.whatsapp_user_id;
      if (!phoneKey) return;
      // Prefer polled status (same as single-call), fallback to batch recipient status
      const polledKey = r.conversation_id || phoneKey;
      const status = polledStatuses[polledKey] || r.status;
      if (status) statuses[phoneKey] = status;
    });
    onStatusUpdate(prev => ({ ...prev, ...statuses }));
  }, [onStatusUpdate]);

  // Poll a single conversation status (same endpoint as single WhatsApp calls)
  const pollRecipientConversation = useCallback((conversationId, phoneKey) => {
    const terminalStatuses = new Set(['done', 'failed', 'error', 'cancelled']);

    const fetchOnce = async () => {
      try {
        const res = await fetch(`${API_BASE}/elevenlabs/conversations/${conversationId}/status`);
        const data = await res.json();
        if (data.success) {
          setRecipientPolledStatuses(prev => ({ ...prev, [conversationId]: data.status }));
          // Always store call outcome — fallback chain: call_successful → error_reason → termination_reason → 'N/A'
          const callOutcome = data.call_successful || data.error_reason || data.termination_reason || 'N/A';
          setRecipientCallSuccessful(prev => ({ ...prev, [conversationId]: callOutcome }));
          // Also sync to Dashboard
          if (onStatusUpdate && phoneKey) {
            onStatusUpdate(prev => ({ ...prev, [phoneKey]: data.status }));
          }
          const baseStatus = data.status.split('-')[0];
          return terminalStatuses.has(baseStatus);
        }
        return true; // stop on error
      } catch {
        return true; // stop on error
      }
    };

    // Immediate first fetch
    fetchOnce().then(isTerminal => {
      if (isTerminal) return;
      // Continue polling every 5s until terminal
      const interval = setInterval(async () => {
        const done = await fetchOnce();
        if (done) clearInterval(interval);
      }, 5000);
    });
  }, [onStatusUpdate]);

  // When batch details load, start polling each recipient that has a conversation_id
  const startPollingRecipients = useCallback((details) => {
    if (!details?.recipients) return;
    details.recipients.forEach(r => {
      if (r.conversation_id && !pollingSet.has(r.conversation_id)) {
        const phoneKey = r.phone_number || r.whatsapp_user_id;
        setPollingSet(prev => new Set(prev).add(r.conversation_id));
        pollRecipientConversation(r.conversation_id, phoneKey);
      }
    });
  }, [pollingSet, pollRecipientConversation]);



  useEffect(() => {
    fetchBatchCalls();
  }, [fetchBatchCalls]);

  // Auto-refresh list every 10 s while any batch is still active
  useEffect(() => {
    const activeStatuses = new Set(['pending', 'in_progress', 'processing']);
    const hasActive = batchCalls.some(b => activeStatuses.has(b.status));
    if (!hasActive) return;
    const timer = setInterval(fetchBatchCalls, 10000);
    return () => clearInterval(timer);
  }, [batchCalls, fetchBatchCalls]);

  // Auto-refresh the expanded batch details every 10 s while it is active
  useEffect(() => {
    if (!expandedBatch) return;
    const activeStatuses = new Set(['pending', 'in_progress', 'processing']);
    const batch = batchCalls.find(b => (b.id || b.batch_call_id) === expandedBatch);
    if (!batch || !activeStatuses.has(batch.status)) return;
    const timer = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE}/elevenlabs/batch-calls/${expandedBatch}`);
        const data = await response.json();
        if (data.success) {
          setBatchDetails(prev => ({ ...prev, [expandedBatch]: data.data }));
          syncStatusesToDashboard(data.data, recipientPolledStatuses);
          startPollingRecipients(data.data);
          
          // Poll lead statuses on interval tick
          if (data.data?.recipients) {
            const conversationIds = data.data.recipients
              .map(r => r.conversation_id)
              .filter(Boolean);
            if (conversationIds.length > 0) {
              fetchRecipientStatuses(conversationIds);
            }
          }
        }
      } catch {}
    }, 10000);
    return () => clearInterval(timer);
  }, [expandedBatch, batchCalls, syncStatusesToDashboard, startPollingRecipients, recipientPolledStatuses, fetchRecipientStatuses]);

  // Expose refresh method to parent via ref
  useImperativeHandle(ref, () => ({
    refresh: fetchBatchCalls
  }));

  const fetchBatchDetails = async (batchId) => {
    if (batchDetails[batchId]) {
      setExpandedBatch(expandedBatch === batchId ? null : batchId);
      // Even if cached, re-poll recipients for fresh data
      if (expandedBatch !== batchId && batchDetails[batchId]) {
        startPollingRecipients(batchDetails[batchId]);
        if (batchDetails[batchId]?.recipients) {
          const conversationIds = batchDetails[batchId].recipients
            .map(r => r.conversation_id || r.conversationId)
            .filter(Boolean);
          if (conversationIds.length > 0) {
            fetchRecipientStatuses(conversationIds);
          }
        }
      }
      return;
    }
    setLoadingDetails(prev => ({ ...prev, [batchId]: true }));
    setExpandedBatch(batchId);
    try {
      const response = await fetch(`${API_BASE}/elevenlabs/batch-calls/${batchId}`);
      const data = await response.json();
      if (data.success) {
        setBatchDetails(prev => ({ ...prev, [batchId]: data.data }));
        syncStatusesToDashboard(data.data, recipientPolledStatuses);
        startPollingRecipients(data.data);
        // Fetch lead statuses for recipients by conversation ID
        if (data.data?.recipients) {
          const conversationIds = data.data.recipients
            .map(r => r.conversation_id || r.conversationId)
            .filter(Boolean);
          if (conversationIds.length > 0) {
            fetchRecipientStatuses(conversationIds);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching batch details:', err);
    } finally {
      setLoadingDetails(prev => ({ ...prev, [batchId]: false }));
    }
  };



  const handleCreate = async (e) => {
    e.preventDefault();
    if (!recipientInput.trim()) {
      onShowToast('Please provide at least one phone number', 'error');
      return;
    }

    setCreating(true);
    try {
      // Parse phone numbers (one per line or comma-separated)
      const phoneNumbers = recipientInput
        .split(/[\n,]+/)
        .map(n => n.trim())
        .filter(n => n.length > 0);

      if (phoneNumbers.length === 0) {
        onShowToast('No valid phone numbers provided', 'error');
        return;
      }

      const recipients = phoneNumbers.map(phone => ({
        phone_number: phone,
        whatsapp_user_id: phone,
      }));

      const name = callName.trim() || `Batch ${new Date().toLocaleString()}`;

      const response = await fetch(`${API_BASE}/elevenlabs/batch-calls`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ callName: name, recipients })
      });

      const data = await response.json();
      if (data.success) {
        onShowToast(`Batch call "${name}" created with ${phoneNumbers.length} recipients`, 'success');
        setCallName('');
        setRecipientInput('');
        setShowCreateForm(false);
        fetchBatchCalls();
      } else {
        onShowToast(data.error || 'Failed to create batch call', 'error');
      }
    } catch (err) {
      onShowToast('Error creating batch call: ' + err.message, 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = async (batchId) => {
    try {
      const response = await fetch(`${API_BASE}/elevenlabs/batch-calls/${batchId}/cancel`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        onShowToast('Batch call cancelled', 'success');
        // Clear cached details so the next expand fetches fresh data
        setBatchDetails(prev => { const n = { ...prev }; delete n[batchId]; return n; });
        fetchBatchCalls();
      } else {
        onShowToast(data.error || 'Failed to cancel', 'error');
      }
    } catch (err) {
      onShowToast('Error cancelling batch call', 'error');
    }
  };

  const handleRetry = async (batchId) => {
    try {
      const response = await fetch(`${API_BASE}/elevenlabs/batch-calls/${batchId}/retry`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        onShowToast('Batch call retry initiated', 'success');
        // Clear cached details so the next expand fetches fresh data
        setBatchDetails(prev => { const n = { ...prev }; delete n[batchId]; return n; });
        fetchBatchCalls();
      } else {
        onShowToast(data.error || 'Failed to retry', 'error');
      }
    } catch (err) {
      onShowToast('Error retrying batch call', 'error');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    // Handle unix timestamps (seconds)
    if (typeof dateStr === 'number') {
      return new Date(dateStr * 1000).toLocaleString();
    }
    return new Date(dateStr).toLocaleString();
  };



  return (
    <div className="batch-call-panel">
      <div className="batch-call-header">
        <div className="batch-call-title">
          <PhoneForwarded />
          <h2>Batch Calls</h2>
        </div>
        <div className="batch-call-actions">
          <button className="batch-btn secondary" onClick={fetchBatchCalls} disabled={loading}>
            <RefreshCw className={loading ? 'spin' : ''} />
            Refresh
          </button>
          <button className="batch-btn primary" onClick={() => setShowCreateForm(!showCreateForm)}>
            {showCreateForm ? <X /> : <Plus />}
            {showCreateForm ? 'Cancel' : 'New Batch Call'}
          </button>
        </div>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="batch-create-form">
          <h3>Create New Batch Call</h3>
          <form onSubmit={handleCreate}>
            <div className="form-group">
              <label>Call Name <span className="hint">(optional)</span></label>
              <input
                type="text"
                placeholder="e.g., Morning outreach campaign (auto-generated if empty)"
                value={callName}
                onChange={(e) => setCallName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Phone Numbers <span className="hint">(one per line or comma-separated)</span></label>
              <textarea
                placeholder={"+1234567890\n+0987654321\n+1122334455"}
                rows={6}
                value={recipientInput}
                onChange={(e) => setRecipientInput(e.target.value)}
                required
              />
            </div>
            <div className="form-actions">
              <button type="button" className="batch-btn secondary" onClick={() => setShowCreateForm(false)}>
                Cancel
              </button>
              <button type="submit" className="batch-btn primary" disabled={creating}>
                {creating ? <Loader2 className="spin" /> : <PhoneForwarded />}
                {creating ? 'Creating...' : 'Submit Batch Call'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Batch Calls List */}
      <div className="batch-calls-list">
        {loading ? (
          <div className="batch-loading">
            <Loader2 className="spin" />
            <p>Loading batch calls...</p>
          </div>
        ) : batchCalls.length === 0 ? (
          <div className="batch-empty">
            <PhoneForwarded />
            <h3>No batch calls yet</h3>
            <p>Create your first batch call to get started</p>
          </div>
        ) : (
          batchCalls.map((batch) => {
            const batchId = batch.id || batch.batch_call_id;
            const isExpanded = expandedBatch === batchId;
            const details = batchDetails[batchId];
            const isLoadingDetail = loadingDetails[batchId];

            return (
              <div key={batchId} className="batch-card">
                <div 
                  className="batch-card-header"
                  onClick={() => fetchBatchDetails(batchId)}
                >
                  <div className="batch-card-info">
                    <div className="batch-card-name">
                      <PhoneForwarded />
                      <span>{batch.name || batch.call_name || batchId}</span>
                    </div>
                    <div className="batch-card-meta">
                      <span className="batch-status-badge">
                        <PhoneForwarded className="batch-status-icon" />
                        {batch.status || 'N/A'}
                      </span>
                      {(batch.created_at_unix || batch.created_at) && (
                        <span className="batch-date">
                          <Clock size={14} />
                          {formatDate(batch.created_at_unix || batch.created_at)}
                        </span>
                      )}
                      <span className="batch-phone-count">
                        <Users size={14} />
                        {details?.recipients?.length || batch.total_calls_scheduled || batch.num_recipients || '—'} recipients
                      </span>
                    </div>
                  </div>
                  <div className="batch-card-expand">
                    {isExpanded ? <ChevronUp /> : <ChevronDown />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="batch-card-details">
                    {isLoadingDetail ? (
                      <div className="batch-detail-loading">
                        <Loader2 className="spin" />
                        <span>Loading details...</span>
                      </div>
                    ) : details ? (
                      <>
                        {/* Summary Stats — uses exact ElevenLabs status values */}
                        {/* Batch status: pending | in_progress | completed | failed | cancelled */}
                        {/* Recipient status: initiated | in-progress | processing | done | failed */}
                        <div className="batch-stats">
                          <div className="batch-stat">
                            <span className="batch-stat-value">{details.total_calls_scheduled ?? 'N/A'}</span>
                            <span className="batch-stat-label">Scheduled</span>
                          </div>
                          <div className="batch-stat">
                            <span className="batch-stat-value">{details.total_calls_dispatched ?? 'N/A'}</span>
                            <span className="batch-stat-label">Dispatched</span>
                          </div>
                          <div className="batch-stat success">
                            <span className="batch-stat-value">{details.total_calls_finished ?? 'N/A'}</span>
                            <span className="batch-stat-label">Finished</span>
                          </div>
                          <div className="batch-stat danger">
                            <span className="batch-stat-value">
                              {details.recipients?.filter(r => r.status === 'failed' || r.status === 'error').length ?? 'N/A'}
                            </span>
                            <span className="batch-stat-label">Failed</span>
                          </div>
                        </div>

                        {/* Recipients List */}
                        {details.recipients && details.recipients.length > 0 && (
                          <div className="batch-recipients">
                            <h4>Recipients ({details.recipients.length})</h4>
                            <div className="recipients-list">
                              {details.recipients.map((recipient, idx) => {
                                const conversationId = recipient.conversation_id || recipient.conversationId;
                                // Green badge: Batch recipient status (from ElevenLabs batch API)
                                const batchStatus = recipient.status || 'N/A';
                                
                                // Blue badge: Conversation status (from our polling/DB - what we save)
                                const conversationStatus = conversationId
                                  ? recipientPolledStatuses[conversationId]
                                  : null;

                                // Yellow badge: Call successful (raw value from conversation API)
                                const callSuccessful = conversationId
                                  ? (recipientCallSuccessful[conversationId] || null)
                                  : null;

                                // Get lead status and notes for this recipient by conversation_id
                                const recipientStatus = conversationId 
                                  ? recipientLeadStatuses[conversationId] 
                                  : null;
                                const leadStatus = recipientStatus?.leadStatus;
                                const notes = recipientStatus?.notes;
                                
                                // Phone number for display
                                const phoneNumber = recipient.phone_number || recipient.whatsapp_user_id;

                                const leadStatusOptions = [
                                  { value: 'lead', label: 'Lead', color: '#3b82f6' },
                                  { value: 'prospect', label: 'Prospect', color: '#f59e0b' },
                                  { value: 'interested', label: 'Interested', color: '#06b6d4' },
                                  { value: 'not-interested', label: 'Not Interested', color: '#f97316' },
                                  { value: 'negotiation', label: 'Negotiation', color: '#8b5cf6' },
                                  { value: 'done', label: 'Done', color: '#10b981' },
                                  { value: 'lost', label: 'Lost', color: '#ef4444' }
                                ];
                                const statusOption = leadStatusOptions.find(opt => opt.value === leadStatus);
                                
                                return (
                                  <div key={idx} className="recipient-item">
                                    <div className="recipient-left">
                                      <div className="recipient-phone">
                                        <Phone size={14} />
                                        <span>{phoneNumber || 'Unknown'}</span>
                                      </div>
                                      {/* Lead Status Badge */}
                                      {statusOption && (
                                        <button
                                          className="recipient-lead-status"
                                          style={{ backgroundColor: statusOption.color + '20', borderColor: statusOption.color, color: statusOption.color }}
                                          onClick={() => setNotesModalData({ phoneNumber, leadStatus, notes })}
                                          title={notes ? 'View notes' : statusOption.label}
                                        >
                                          <Search size={14} />
                                          {statusOption.label}
                                        </button>
                                      )}
                                    </div>
                                    <div className="recipient-statuses">
                                      {/* Green badge: Batch API status (big phone icon) */}
                                      <span className="recipient-status-batch" title="Batch API status">
                                        <Phone className="status-icon" size={20} />
                                        {batchStatus}
                                      </span>
                                      {/* Blue badge: Conversation status (info icon) */}
                                      {conversationStatus && (
                                        <span className="recipient-status-conversation" title="Conversation status (saved to DB)">
                                          <Info className="status-icon" size={16} />
                                          {conversationStatus}
                                        </span>
                                      )}
                                      {/* Yellow badge: Call successful (target icon = main status) */}
                                      <span className="recipient-status-call-successful" title="Call outcome">
                                        <Target className="status-icon" size={16} />
                                        {callSuccessful || 'N/A'}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="batch-detail-actions">
                          {(batch.status === 'in_progress' || batch.status === 'pending' || batch.status === 'processing') && (
                            <button 
                              className="batch-btn danger small" 
                              onClick={(e) => { e.stopPropagation(); handleCancel(batchId); }}
                            >
                              <XCircle size={14} />
                              Cancel
                            </button>
                          )}
                          {(batch.status === 'completed' || batch.status === 'failed') && (
                            <button 
                              className="batch-btn secondary small" 
                              onClick={(e) => { e.stopPropagation(); handleRetry(batchId); }}
                            >
                              <RotateCcw size={14} />
                              Retry Failed
                            </button>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="batch-detail-error">
                        <AlertCircle />
                        <span>Could not load details</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Notes Modal for Recipient */}
      <NotesModal 
        isOpen={!!notesModalData}
        notes={notesModalData?.notes}
        onClose={() => setNotesModalData(null)}
        title="Lead Notes"
        phone={notesModalData?.phoneNumber}
        leadStatus={notesModalData?.leadStatus}
      />
    </div>
  );
});

BatchCallPanel.displayName = 'BatchCallPanel';

export default BatchCallPanel;
